import React, { useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import "./ProjectDetail.css";
import projects from "./projectsData";
import { HiArrowLeft, HiExternalLink, HiCheckCircle, HiLockClosed } from "react-icons/hi";
import { BsGithub } from "react-icons/bs";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = projects.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!project) {
    return (
      <div className="pd-not-found">
        <h2>Project not found</h2>
        <button className="pd-back-btn" onClick={() => navigate("/")}>
          <HiArrowLeft /> Back to Home
        </button>
      </div>
    );
  }

  const others = projects.filter((p) => p.id !== id).slice(0, 3);
  const isClient = project.isClientProject;

  return (
    <div className="pd-page">
      {/* ── Hero ── */}
      <div className="pd-hero">
        <div className="pd-hero-img-wrap">
          <img src={project.img} alt={project.title} className="pd-hero-img" />
          <div className="pd-hero-overlay" />
        </div>

        <div className="pd-hero-content">
          <button className="pd-back-btn" onClick={() => navigate(-1)}>
            <HiArrowLeft /> Back
          </button>

          <div className="pd-hero-badges">
            <span className="pd-type-badge">{project.type}</span>
            {isClient && (
              <span className="pd-client-badge">
                <HiLockClosed /> Client Project
              </span>
            )}
          </div>

          <h1 className="pd-title">{project.title}</h1>
          <p className="pd-subtitle">{project.subtitle}</p>

          {/* Hero action buttons */}
          {isClient ? (
            <div className="pd-nda-notice">
              <HiLockClosed />
              <span>
                Source code and live demo are restricted under a client NDA.
                Details available on request.
              </span>
            </div>
          ) : (
            <div className="pd-hero-actions">
              <a
                href={project.liveLink}
                target="_blank"
                rel="noreferrer"
                className="pd-btn-primary"
              >
                <HiExternalLink />
                Live Demo
              </a>
              <a
                href={project.githubLink}
                target="_blank"
                rel="noreferrer"
                className="pd-btn-secondary"
              >
                <BsGithub />
                View Code
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      <div className="pd-body">
        {/* Tags */}
        <div className="pd-tags">
          {project.tags.map((tag) => (
            <span className="pd-tag" key={tag}>{tag}</span>
          ))}
        </div>

        {/* Two-column layout */}
        <div className="pd-layout">
          {/* Left — overview + highlights */}
          <div className="pd-left">
            <div className="pd-section">
              <h2 className="pd-section-title">Overview</h2>
              <p className="pd-overview">{project.overview}</p>
            </div>

            <div className="pd-section">
              <h2 className="pd-section-title">Key Highlights</h2>
              <ul className="pd-highlights">
                {project.highlights.map((h, i) => (
                  <li key={i} className="pd-highlight-item">
                    <span className="pd-highlight-icon">
                      <HiCheckCircle />
                    </span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right — tech details card */}
          <div className="pd-right">
            <div className="pd-tech-card">
              <h3 className="pd-tech-card-title">Tech Stack</h3>
              <div className="pd-tech-list">
                {project.techDetails.map((item) => (
                  <div className="pd-tech-item" key={item.label}>
                    <span className="pd-tech-label">{item.label}</span>
                    <span className="pd-tech-value">{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Card actions — conditional on client project */}
              {isClient ? (
                <div className="pd-tech-nda">
                  <HiLockClosed />
                  <span>Links restricted under NDA</span>
                </div>
              ) : (
                <div className="pd-tech-card-actions">
                  <a
                    href={project.liveLink}
                    target="_blank"
                    rel="noreferrer"
                    className="pd-btn-primary pd-btn-full"
                  >
                    <HiExternalLink />
                    Live Demo
                  </a>
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noreferrer"
                    className="pd-btn-secondary pd-btn-full"
                  >
                    <BsGithub />
                    Source Code
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── More Projects ── */}
        {others.length > 0 && (
          <div className="pd-more">
            <h2 className="pd-more-title">More Projects</h2>
            <div className="pd-more-grid">
              {others.map((p) => (
                <NavLink
                  to={`/project/${p.id}`}
                  key={p.id}
                  className="pd-more-card"
                >
                  <div className="pd-more-img-wrap">
                    <img src={p.img} alt={p.title} />
                    <div className="pd-more-overlay" />
                    <span className="pd-more-badge">{p.type}</span>
                  </div>
                  <div className="pd-more-body">
                    <h4 className="pd-more-card-title">{p.title}</h4>
                    <p className="pd-more-card-desc">{p.shortDesc}</p>
                    <div className="pd-more-tags">
                      {p.tags.slice(0, 3).map((t) => (
                        <span className="pd-tag" key={t}>{t}</span>
                      ))}
                    </div>
                  </div>
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
